import { fetchAllPlanStatuses } from "api/SubscriptionApiService";
import accountTypes from "constants/data/AccountTypes";
import { StatusRecord } from "interface/FetchAllPlanStatusesOpts";
import { createContext, useContext, useEffect, useState } from "react";

export interface PlanStatusContextType {
    loading: boolean;
    statuses: StatusRecord | null;
    activeAccountTypes: typeof accountTypes;
    refresh: () => void;
}

const PlanStatusContext = createContext<PlanStatusContextType | null>(null);

// Fallback: all account types active except sandbox
function defaultActiveStatuses(): StatusRecord {
    return Object.fromEntries(
        accountTypes.map(({ name }) => [name, name !== 'sandbox'])
    ) as StatusRecord;
}

/**
 * Provides plan status context to its children components.
 * 
 * Steps:
 * 1. Initializes loading state and statuses.
 * 2. Fetches plan statuses from the API on mount or when nonce changes.
 * 3. Updates the context with loading state, fetched statuses, and active account types.
 * 4. Provides a refresh function to re-fetch the statuses.
 * 
 * useNonce is used to trigger re-fetching of plan statuses when needed.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components that will have access to the plan status context.
 * @returns {JSX.Element} The rendered provider component.
 * 
 * @example
 * // Example usage:
 * <PlanStatusProvider>
 *   <MainComponent />
 * </PlanStatusProvider>
 */
export function PlanStatusProvider({ children }: { children: React.ReactNode }) {
    const [loading, setLoading] = useState(true);
    const [statuses, setStatuses] = useState<StatusRecord | null>(null);
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        setLoading(true)

        fetchAllPlanStatuses({ tag: 'PlanStatusProvider' })
            .then(({ statuses }) => {
                setStatuses(statuses);
            })
            .catch((e) => {
                console.error("Failed to fetch plan statuses:", e);
                setStatuses(defaultActiveStatuses());
            })
            .finally(() => setLoading(false))
    }, [nonce])

    const activeAccountTypes = statuses
        ? accountTypes.filter(({ name }) => statuses[name])
        : [];

    const refresh = () => setNonce((n) => n + 1);

    return (
        <PlanStatusContext.Provider value={{ loading, statuses, activeAccountTypes, refresh }}>
            {children}
        </PlanStatusContext.Provider>
    );
}

export function usePlanStatus() {
    const context = useContext(PlanStatusContext);
    if (!context) {
        throw new Error("usePlanStatus must be used within a PlanStatusProvider");
    }
    return context;
}